import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Loader2, AlertCircle, ShieldCheck, CheckCircle2 } from 'lucide-react';

// OAuth 2.1 authorization endpoint (user-facing).
// The MCP client redirects the browser here with ?client_id=...&redirect_uri=...&code_challenge=...&state=...
// We show a consent UI, then on Approve we call the backend to mint an authorization code
// and redirect back to `redirect_uri?code=...&state=...`.

interface ClientInfo {
  client_id: string;
  client_name: string;
  redirect_uris: string[];
}

interface OAuthParams {
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string | null;
  scope: string | null;
  response_type: string;
}

function parseParams(search: string): OAuthParams | { error: string } {
  const p = new URLSearchParams(search);
  const responseType = p.get('response_type');
  if (responseType !== 'code') return { error: 'Only response_type=code is supported' };
  const clientId = p.get('client_id');
  const redirectUri = p.get('redirect_uri');
  const codeChallenge = p.get('code_challenge');
  const codeChallengeMethod = p.get('code_challenge_method') || 'S256';
  if (!clientId || !redirectUri || !codeChallenge) {
    return { error: 'Missing required parameters: client_id, redirect_uri, code_challenge' };
  }
  if (codeChallengeMethod !== 'S256') return { error: 'code_challenge_method must be S256' };
  return {
    client_id: clientId,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod,
    state: p.get('state'),
    scope: p.get('scope'),
    response_type: responseType,
  };
}

function redirectBackWithError(redirectUri: string, state: string | null, error: string) {
  const url = new URL(redirectUri);
  url.searchParams.set('error', error);
  if (state) url.searchParams.set('state', state);
  window.location.href = url.toString();
}

// Redirect via a real HTML form submission instead of `window.location.href`.
// Some embedded webviews (Claude Desktop's OAuth popup in particular) detect
// "real" form submissions as user-initiated navigations, whereas JS-driven
// location changes are sometimes missed — causing the client to not refresh
// its UI after the OAuth flow completes.
function redirectBackWithCode(redirectUri: string, code: string, state: string | null) {
  const form = document.createElement('form');
  form.method = 'GET';
  form.action = redirectUri;
  // Use _self so the navigation happens in the current window (not a new tab).
  form.target = '_self';

  const codeInput = document.createElement('input');
  codeInput.type = 'hidden';
  codeInput.name = 'code';
  codeInput.value = code;
  form.appendChild(codeInput);

  if (state) {
    const stateInput = document.createElement('input');
    stateInput.type = 'hidden';
    stateInput.name = 'state';
    stateInput.value = state;
    form.appendChild(stateInput);
  }

  document.body.appendChild(form);
  form.submit();
}

export function OAuthAuthorize() {
  const { user, loading: authLoading } = useAuth();
  const [params, setParams] = useState<OAuthParams | null>(null);
  const [paramError, setParamError] = useState<string | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [approved, setApproved] = useState<{ code: string; state: string | null } | null>(null);
  // True once we've actually fired the redirect. If the form submit genuinely
  // navigates away, this component unmounts and the user never sees it. If
  // the navigation is intercepted by the client's webview (as often happens
  // with Claude Desktop — it grabs the code from the URL and cancels the
  // navigation before the browser actually leaves our page), we show a
  // "completed" state so the user gets clear feedback that they can close
  // the window manually.
  const [completed, setCompleted] = useState(false);

  // Parse URL params once on mount.
  useEffect(() => {
    const result = parseParams(window.location.search);
    if ('error' in result) setParamError(result.error);
    else setParams(result);
  }, []);

  // If not logged in, store the current URL and redirect to /login.
  // After successful login, AuthCallback will restore this URL.
  useEffect(() => {
    if (authLoading) return;
    if (!user && params) {
      sessionStorage.setItem('pending_oauth_request', window.location.href);
      window.location.href = '/login';
    }
  }, [authLoading, user, params]);

  // Fetch client metadata so we can show the client name on the consent screen.
  useEffect(() => {
    if (!params || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/oauth/clients/${encodeURIComponent(params.client_id)}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setFetchError(body.error_description || body.error || `HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as ClientInfo;
        if (!cancelled) {
          // Verify the redirect_uri matches one registered for this client.
          if (!data.redirect_uris.includes(params.redirect_uri)) {
            setFetchError('redirect_uri not registered for this client');
            return;
          }
          setClientInfo(data);
        }
      } catch (e) {
        if (!cancelled) setFetchError(e instanceof Error ? e.message : 'Failed to fetch client info');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, user]);

  async function handleApprove() {
    if (!params || !clientInfo) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        throw new Error('No active session');
      }
      const session = sessionData.session;

      const res = await fetch('/oauth/authorize/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
          code_challenge: params.code_challenge,
          code_challenge_method: params.code_challenge_method,
          state: params.state,
          scope: params.scope,
          supabase_refresh_token: session.refresh_token,
        }),
      });

      const body = await res.json();
      if (!res.ok) throw new Error(body.error_description || body.error || `HTTP ${res.status}`);
      if (!body.code) throw new Error('Backend did not return an authorization code');

      // Show success state first, then fire the actual redirect after a short
      // delay. This gives the user visual confirmation and lets Claude Desktop's
      // webview settle before the navigation.
      setApproved({ code: body.code, state: params.state });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Approval failed');
      setSubmitting(false);
    }
  }

  // Once approved, show the success screen and auto-redirect after 1.2s via a
  // real form submission (not window.location.href — some webviews don't
  // detect JS-driven navigations and never refresh their UI).
  //
  // If the navigation actually happens the component unmounts and we never
  // see the completed state. If the webview intercepts and cancels the
  // navigation (Claude Desktop does this), we'll still be mounted after
  // ~2.5s and we flip to the "completed" state to tell the user everything
  // worked and they can close the window.
  useEffect(() => {
    if (!approved || !params) return;
    const redirectTimer = window.setTimeout(() => {
      redirectBackWithCode(params.redirect_uri, approved.code, approved.state);
      // Try to close the popup as soon as the redirect fires — if we were
      // opened via window.open(), this works; otherwise it's a no-op.
      window.setTimeout(() => {
        try { window.close(); } catch { /* ignore */ }
      }, 400);
    }, 1200);
    const completedTimer = window.setTimeout(() => {
      setCompleted(true);
    }, 2500);
    return () => {
      window.clearTimeout(redirectTimer);
      window.clearTimeout(completedTimer);
    };
  }, [approved, params]);

  function handleDeny() {
    if (!params) return;
    redirectBackWithError(params.redirect_uri, params.state, 'access_denied');
  }

  // --- Render ---

  if (paramError) {
    return (
      <CenteredCard>
        <div className="w-14 h-14 rounded-2xl bg-accent-rose-dim flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-7 h-7 text-accent-rose" />
        </div>
        <h2 className="text-lg font-semibold text-text-primary mb-2">Richiesta non valida</h2>
        <p className="text-sm text-text-tertiary">{paramError}</p>
      </CenteredCard>
    );
  }

  if (authLoading || !params || !user) {
    return <FullScreenSpinner label="Verifica sessione..." />;
  }

  if (fetchError) {
    return (
      <CenteredCard>
        <div className="w-14 h-14 rounded-2xl bg-accent-rose-dim flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-7 h-7 text-accent-rose" />
        </div>
        <h2 className="text-lg font-semibold text-text-primary mb-2">Client non valido</h2>
        <p className="text-sm text-text-tertiary">{fetchError}</p>
      </CenteredCard>
    );
  }

  if (!clientInfo) {
    return <FullScreenSpinner label="Caricamento..." />;
  }

  // Success state: shown after the user clicks Authorize and the backend has
  // minted the auth code.
  //
  // - For the first ~2.5s after approval we show "Reindirizzamento in corso",
  //   and in the middle of that window we actually fire the form submit.
  // - If the navigation happens, the component unmounts and the user sees
  //   whatever the client redirects them to.
  // - If the client (e.g. Claude Desktop) intercepts and cancels the
  //   navigation without unloading the page, the 2.5s timer flips us to the
  //   `completed` state, giving the user a clear "done, you can close this
  //   window" message instead of leaving them on the loading spinner.
  if (approved) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial px-4">
        <div className="w-full max-w-md bg-surface-secondary rounded-2xl shadow-xl p-8 animate-fade-in text-center">
          <div className="w-16 h-16 rounded-2xl bg-accent-emerald/15 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-accent-emerald" />
          </div>
          <h1 className="text-lg font-bold text-text-primary mb-2">
            {completed ? 'Tutto pronto!' : 'Autorizzazione concessa'}
          </h1>
          <p className="text-sm text-text-tertiary mb-5">
            <strong className="text-text-secondary">{clientInfo.client_name}</strong> ora può accedere
            ai tuoi dati Zenda.
          </p>
          {completed ? (
            <>
              <p className="text-sm text-text-secondary mb-4">
                Puoi chiudere questa finestra e tornare al client AI.
              </p>
              <button
                onClick={() => {
                  try { window.close(); } catch { /* ignore */ }
                }}
                className="px-5 py-2 rounded-lg bg-accent-violet text-white text-sm font-medium hover:bg-accent-violet-light transition-colors shadow-lg shadow-accent-violet/20"
              >
                Chiudi finestra
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 text-xs text-text-quaternary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Reindirizzamento in corso...
              </div>
              <button
                onClick={() => redirectBackWithCode(params.redirect_uri, approved.code, approved.state)}
                className="mt-4 text-[11px] text-accent-violet hover:underline"
              >
                Reindirizza manualmente
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial px-4">
      <div className="w-full max-w-md bg-surface-secondary rounded-2xl shadow-xl p-8 animate-fade-in">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20 shrink-0">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-text-primary truncate">
              Autorizza <span className="text-accent-violet">{clientInfo.client_name}</span>
            </h1>
            <p className="text-xs text-text-quaternary">Accesso ai tuoi dati Zenda via MCP</p>
          </div>
        </div>

        <div className="bg-surface-primary rounded-xl p-4 mb-6">
          <p className="text-sm text-text-secondary mb-3">
            <strong className="text-text-primary">{clientInfo.client_name}</strong> richiede l'accesso per:
          </p>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-accent-emerald mt-0.5">✓</span>
              <span>Leggere i tuoi task, progetti, spazi e report</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent-emerald mt-0.5">✓</span>
              <span>Creare e modificare task per tuo conto</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent-emerald mt-0.5">✓</span>
              <span>Registrare tempo sui task</span>
            </li>
          </ul>
          <p className="mt-3 text-xs text-text-quaternary">
            Loggato come <strong className="text-text-tertiary">{user.email}</strong>
          </p>
          <p className="mt-2 text-xs text-text-quaternary break-all">
            Verrai reindirizzato a: <span className="font-mono">{params.redirect_uri}</span>
          </p>
        </div>

        {submitError && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-sm text-red-400">{submitError}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleDeny}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 rounded-lg border border-border-primary text-text-secondary text-sm font-medium hover:bg-surface-tertiary transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={handleApprove}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 rounded-lg bg-accent-violet text-white text-sm font-medium hover:bg-accent-violet-light transition-colors shadow-lg shadow-accent-violet/20 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Autorizzazione...' : 'Autorizza'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial px-4">
      <div className="text-center animate-fade-in max-w-sm">{children}</div>
    </div>
  );
}

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
      <div className="flex flex-col items-center gap-5 animate-fade-in">
        <div className="relative">
          <div className="w-14 h-14 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20">
            <span className="text-lg font-bold text-white">Z</span>
          </div>
          <div className="absolute -right-1 -bottom-1 w-6 h-6 rounded-full bg-surface-primary flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-accent-violet animate-spin" />
          </div>
        </div>
        <p className="text-sm font-semibold text-text-primary">{label}</p>
      </div>
    </div>
  );
}
